import posthog from "posthog-js";
import { NetworkBalance, TokenBalance } from "@/Models/Balance";
import { BalanceProvider } from "@/Models/BalanceProvider";
import { Network } from "@/Models/Network";
import { classifyNodeError } from "./nodeErrorClassifier";
import { extractErrorDetails } from "./errorUtils";
import {
    AztecBalanceProvider,
    EVMBalanceProvider,
    FuelBalanceProvider,
    SolanaBalanceProvider,
    StarknetBalanceProvider
    //BitcoinBalanceProvider,
    //LoopringBalanceProvider,
    //ParadexBalanceProvider,
    //QueryBalanceProvider,
    //TonBalanceProvider,
    //TronBalanceProvider,
    //ZkSyncBalanceProvider,
    //HyperliquidBalanceProvider
} from "./providers";

function formatErrorBalances(errorBalances: TokenBalance[]) {
    return errorBalances.map(b => ({
        token: b.token,
        error_message: b.error?.message,
        error_name: b.error?.name,
        error_code: b.error?.code,
        error_category: b.error?.category,
        response_status: b.error?.status,
        response_status_text: b.error?.statusText,
        request_url: b.error?.requestUrl,
        // Include first 500 chars of stack trace for debugging
        error_stack: b.error?.stack?.substring(0, 500),
        // Include response data if available (truncated for size)
        response_data: b.error?.responseData
            ? JSON.stringify(b.error.responseData).substring(0, 1000)
            : undefined
    }));
}

export class BalanceResolver {

    private providers: BalanceProvider[] = [
        // new QueryBalanceProvider(),
        new AztecBalanceProvider(),
        new EVMBalanceProvider(),
        new FuelBalanceProvider(),
        // new LoopringBalanceProvider(),
        new SolanaBalanceProvider(),
        new StarknetBalanceProvider(),
        // new TonBalanceProvider(),
        // new ZkSyncBalanceProvider(),
        // new TronBalanceProvider(),
        // // new ParadexBalanceProvider(),
        // new BitcoinBalanceProvider(),
        // new HyperliquidBalanceProvider()
    ];

    async getBalance(network: Network, address?: string, options?: { timeoutMs?: number, retryCount?: number }): Promise<NetworkBalance> {
        try {
            if (!address)
                throw new Error(`No address provided for network ${network.caip2Id}`)
            const provider = this.providers.find(p => p.supportsNetwork(network))
            //TODO: create interface for balance providers in case of empty state they shoudl throw error 
            //never return undefined as SWR does not set loading state if undefined is returned
            if (!provider) throw new Error(`No balance provider found for network ${network.caip2Id}`)
            const balances = await provider.fetchBalance(address, network, { timeoutMs: options?.timeoutMs, retryCount: options?.retryCount })

            const errorBalances = balances?.filter(b => b.error)
            if (errorBalances?.length) {
                const balanceError = new Error(`Could not fetch balance for ${errorBalances.map(t => t.token).join(", ")} in ${network.caip2Id}`);
                posthog.captureException(balanceError, {
                    $layerswap_exception_type: "Balance Error",
                    network: network.caip2Id,
                    node_url: network.nodes[0].url,
                    address: address,
                    failed_tokens: formatErrorBalances(errorBalances),
                    error_categories: [...new Set(errorBalances.map(b => b.error?.category).filter(Boolean))],
                    error_codes: [...new Set(errorBalances.map(b => b.error?.code).filter(Boolean))],
                    http_statuses: [...new Set(errorBalances.map(b => b.error?.status).filter(Boolean))]
                });
            }

            return { balances };
        }
        catch (e) {
            const errorDetails = extractErrorDetails(e);
            const error = new Error(errorDetails.message);
            error.name = "BalanceError";
            error.cause = e;
            posthog.captureException(error, {
                $layerswap_exception_type: "Balance Error",
                network: network.caip2Id,
                node_url: network.nodes[0].url,
                address: address,
                error_category: classifyNodeError(e),
                error_code: errorDetails.code,
                response_status: errorDetails.status,
                response_status_text: errorDetails.statusText,
                response_data: errorDetails.responseData,
                request_url: errorDetails.requestUrl,
            });

            return { balances: [] }
        }
    }
}
